import { useState, useEffect, useMemo, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import TCBadge from "@/components/TCBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  ArrowUpDown, RefreshCw, Search, Check, AlertTriangle, XCircle, ArrowRight,
  Package, TrendingUp, TrendingDown, Filter, Loader2, BarChart3, FileText, Download, Table2
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import axios from "axios";
import { toast } from "sonner";

import { API_URL as API } from "@/lib/api";

const ESTADO_CONFIG = {
  coincide: { label: "Coincide", icon: Check, color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200" },
  diferencia: { label: "Diferencia", icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200" },
  solo_entrega: { label: "Solo Almacén", icon: ArrowRight, color: "text-[#FF9900]", bg: "bg-[#FF9900]/10", border: "border-[#FF9900]/30" },
  solo_ingreso: { label: "Solo Local", icon: Package, color: "text-violet-600", bg: "bg-violet-50", border: "border-violet-200" },
};

const PIE_COLORS = ["#10b981", "#f59e0b", "#3b82f6", "#8b5cf6"];

const CruceDatos = ({ globalTC }) => {
  const [localId, setLocalId] = useState("manco_kapac");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filterFamilia, setFilterFamilia] = useState("__all__");
  const [filterEstado, setFilterEstado] = useState("__all__");
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [sortKey, setSortKey] = useState("fecha");
  const [sortDir, setSortDir] = useState("asc");
  const [showFilters, setShowFilters] = useState(false);
  const [exporting, setExporting] = useState(null);

  const handleExport = async (format) => {
    setExporting(format);
    toast.info(`Generando ${format.toUpperCase()}...`);
    try {
      const params = new URLSearchParams({ format });
      if (fechaInicio) params.append("fecha_inicio", fechaInicio);
      if (fechaFin) params.append("fecha_fin", fechaFin);
      if (filterFamilia && filterFamilia !== "__all__") params.append("familia", filterFamilia);
      if (filterEstado && filterEstado !== "__all__") params.append("estado", filterEstado);
      const res = await axios.post(`${API}/api/cruce/${localId}/export/${format}?${params.toString()}`);
      toast.success(`Reporte generado: ${res.data.filename} (${res.data.registros} registros)`);
      // Trigger download
      try { window.top.location.href = `${API}${res.data.download_url}`; }
      catch { window.location.href = `${API}${res.data.download_url}`; }
    } catch (err) {
      toast.error(err.response?.data?.detail || `Error al generar ${format.toUpperCase()}`);
    } finally { setExporting(null); }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fechaInicio) params.append("fecha_inicio", fechaInicio);
      if (fechaFin) params.append("fecha_fin", fechaFin);
      if (filterFamilia && filterFamilia !== "__all__") params.append("familia", filterFamilia);
      const url = `${API}/api/cruce/${localId}?${params.toString()}`;
      const res = await axios.get(url, { timeout: 60000 });
      setData(res.data);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 524 || err.code === "ECONNABORTED") {
        toast.error("Tiempo de espera agotado. Intenta filtrar por fechas o familia para reducir los datos.");
      } else if (detail) {
        toast.error(typeof detail === "string" ? detail : "Error al cargar datos de cruce");
      } else {
        toast.error(`Error al cargar datos de cruce (${status || err.message})`);
      }
    } finally {
      setLoading(false);
    }
  }, [localId, fechaInicio, fechaFin, filterFamilia]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    if (!data?.resultados) return [];
    let result = data.resultados;
    if (search) {
      const s = search.toLowerCase();
      result = result.filter(r => r.codigo.toLowerCase().includes(s) || r.descripcion.toLowerCase().includes(s));
    }
    if (filterEstado !== "__all__") {
      result = result.filter(r => r.estado === filterEstado);
    }
    return result;
  }, [data, search, filterEstado]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let va = a[sortKey] ?? "";
      let vb = b[sortKey] ?? "";
      if (typeof va === "number" && typeof vb === "number") {
        return sortDir === "asc" ? va - vb : vb - va;
      }
      return sortDir === "asc" ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
  }, [filtered, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const kpis = data?.kpis;
  const pieData = kpis ? [
    { name: "Coinciden", value: kpis.coincidencias },
    { name: "Diferencias", value: kpis.diferencias },
    { name: "Solo Almacén", value: kpis.solo_entregas },
    { name: "Solo Local", value: kpis.solo_ingresos },
  ].filter(d => d.value > 0) : [];

  const topDiferencias = useMemo(() => {
    if (!data?.resultados) return [];
    return data.resultados
      .filter(r => r.estado === "diferencia" || r.estado === "solo_entrega" || r.estado === "solo_ingreso")
      .sort((a, b) => Math.abs(b.diferencia) - Math.abs(a.diferencia))
      .slice(0, 10)
      .map(r => ({
        name: r.codigo.length > 18 ? r.codigo.slice(0, 18) + "…" : r.codigo,
        enviado: r.cantidad_enviada,
        recibido: r.cantidad_recibida,
        diff: Math.abs(r.diferencia),
      }));
  }, [data]);

  const columns = [
    { key: "fecha", label: "Fecha", w: "w-24" },
    { key: "codigo", label: "Código", w: "w-40" },
    { key: "descripcion", label: "Descripción", w: "min-w-48" },
    { key: "familia", label: "Familia", w: "w-32" },
    { key: "cantidad_enviada", label: "Enviado", w: "w-20" },
    { key: "cantidad_recibida", label: "Recibido", w: "w-20" },
    { key: "diferencia", label: "Diferencia", w: "w-24" },
    { key: "estado", label: "Estado", w: "w-28" },
  ];

  const resetFilters = () => {
    setSearch(""); setFilterFamilia("__all__"); setFilterEstado("__all__");
    setFechaInicio(""); setFechaFin("");
  };

  return (
    <div className="space-y-4" data-testid="cruce-datos-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-foreground" data-testid="cruce-title">Cruce de Datos</h1>
          <p className="text-xs text-muted-foreground">Comparación: Salidas Almacén vs Ingresos Local</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {globalTC !== null && globalTC !== undefined && <TCBadge tc={globalTC} />}
          <Select value={localId} onValueChange={(v) => { setLocalId(v); setFilterFamilia("__all__"); }}>
            <SelectTrigger className="w-44 h-8 text-xs" data-testid="cruce-local-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="manco_kapac">Manco Kapac</SelectItem>
              <SelectItem value="plaza_bonita">Plaza Bonita</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} className="h-8 text-xs gap-1.5"
            data-testid="cruce-refresh-btn">
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Actualizar
          </Button>
        </div>
      </div>

      {/* Date Range Filter - Always Visible */}
      <Card className="border-[#FF9900]/30 bg-[#FF9900]/5">
        <CardContent className="p-3">
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="flex items-center gap-2 flex-wrap flex-1">
              <div>
                <label className="text-[10px] text-[#FF9900] uppercase font-semibold mb-1 block">Fecha Inicio Comparativa</label>
                <Input type="date" value={fechaInicio} onChange={e => setFechaInicio(e.target.value)}
                  className="h-8 text-xs w-40 bg-card" data-testid="cruce-fecha-inicio" />
              </div>
              <div>
                <label className="text-[10px] text-[#FF9900] uppercase font-semibold mb-1 block">Fecha Fin</label>
                <Input type="date" value={fechaFin} onChange={e => setFechaFin(e.target.value)}
                  className="h-8 text-xs w-40 bg-card" data-testid="cruce-fecha-fin" />
              </div>
              {data?.filtros && (
                <div className="text-[10px] text-muted-foreground leading-relaxed ml-2">
                  <p>Almacén (Quintanilla): datos desde <strong>2023</strong></p>
                  <p>Locales (Mastersoft): datos desde <strong>mediados 2024</strong></p>
                  {data.filtros.fecha_min && <p>Rango disponible: <strong>{data.filtros.fecha_min}</strong> a <strong>{data.filtros.fecha_max}</strong></p>}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button variant="outline" size="sm" disabled={exporting !== null || filtered.length === 0}
                onClick={() => handleExport("pdf")}
                className="h-8 text-xs gap-1.5 border-rose-400 text-rose-700 hover:bg-rose-50"
                data-testid="cruce-export-pdf-btn">
                {exporting === "pdf" ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
                Exportar PDF
              </Button>
              <Button variant="outline" size="sm" disabled={exporting !== null || filtered.length === 0}
                onClick={() => handleExport("excel")}
                className="h-8 text-xs gap-1.5 border-sky-400 text-[#FF9900] hover:bg-[#FF9900]/10"
                data-testid="cruce-export-excel-btn">
                {exporting === "excel" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Table2 className="w-3 h-3" />}
                Exportar Excel
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading && !data ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-[#FF9900]" />
        </div>
      ) : data ? (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="cruce-kpis">
            <Card className="border-border">
              <CardContent className="p-3">
                <p className="text-[10px] uppercase text-muted-foreground font-medium">Total Registros</p>
                <p className="text-2xl font-bold text-foreground">{kpis.total_registros}</p>
              </CardContent>
            </Card>
            <Card className="border-emerald-200 bg-emerald-50/50">
              <CardContent className="p-3">
                <p className="text-[10px] uppercase text-emerald-600 font-medium">Coinciden</p>
                <p className="text-2xl font-bold text-emerald-700">{kpis.coincidencias}</p>
                <p className="text-xs text-emerald-600 font-semibold">{kpis.pct_coincidencia}%</p>
              </CardContent>
            </Card>
            <Card className="border-amber-200 bg-amber-50/50">
              <CardContent className="p-3">
                <p className="text-[10px] uppercase text-amber-600 font-medium">Diferencias</p>
                <p className="text-2xl font-bold text-amber-700">{kpis.diferencias}</p>
              </CardContent>
            </Card>
            <Card className="border-[#FF9900]/30 bg-muted/50">
              <CardContent className="p-3">
                <p className="text-[10px] uppercase text-[#FF9900] font-medium">Solo Almacén</p>
                <p className="text-2xl font-bold text-[#FF9900]">{kpis.solo_entregas}</p>
              </CardContent>
            </Card>
            <Card className="border-violet-200 bg-violet-50/50">
              <CardContent className="p-3">
                <p className="text-[10px] uppercase text-violet-600 font-medium">Solo Local</p>
                <p className="text-2xl font-bold text-violet-700">{kpis.solo_ingresos}</p>
              </CardContent>
            </Card>
            <Card className="border-border">
              <CardContent className="p-3">
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <p className="text-[10px] uppercase text-muted-foreground font-medium">Enviado</p>
                    <p className="text-lg font-bold text-foreground">{kpis.total_enviado.toLocaleString()}</p>
                  </div>
                  <div className="flex-1">
                    <p className="text-[10px] uppercase text-muted-foreground font-medium">Recibido</p>
                    <p className="text-lg font-bold text-foreground">{kpis.total_recibido.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {pieData.length > 0 && (
              <Card>
                <CardHeader className="pb-2 pt-3 px-4">
                  <CardTitle className="text-sm font-semibold text-foreground">Distribución de Resultados</CardTitle>
                </CardHeader>
                <CardContent className="p-2">
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                        outerRadius={80} innerRadius={40} paddingAngle={2} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        labelLine={false} style={{ fontSize: 11 }}>
                        {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => [v, "Registros"]} />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {topDiferencias.length > 0 && (
              <Card>
                <CardHeader className="pb-2 pt-3 px-4">
                  <CardTitle className="text-sm font-semibold text-foreground">Top Diferencias por Producto</CardTitle>
                </CardHeader>
                <CardContent className="p-2">
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={topDiferencias} layout="vertical" margin={{ left: 10, right: 10 }}>
                      <XAxis type="number" tick={{ fontSize: 10 }} />
                      <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 9 }} />
                      <Tooltip contentStyle={{ fontSize: 11 }} />
                      <Bar dataKey="enviado" name="Enviado" fill="#3b82f6" barSize={8} />
                      <Bar dataKey="recibido" name="Recibido" fill="#10b981" barSize={8} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Filters + Table */}
          <Card>
            <div className="px-4 py-2 border-b border-border flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 min-w-48">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <Input placeholder="Buscar por código o descripción..." value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-8 h-8 text-xs" data-testid="cruce-search" />
              </div>
              <Button variant="outline" size="sm" onClick={() => setShowFilters(p => !p)} className="text-xs gap-1 h-8"
                data-testid="cruce-filter-toggle">
                <Filter className="h-3 w-3" /> Filtros
              </Button>
              <span className="text-xs text-muted-foreground">
                <BarChart3 className="w-3.5 h-3.5 inline mr-1" />
                {filtered.length} de {data.resultados.length}
              </span>
            </div>

            {showFilters && (
              <div className="px-4 py-3 bg-muted/80 border-b border-border grid grid-cols-2 md:grid-cols-4 gap-3"
                data-testid="cruce-filters">
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase mb-1 block">Familia</label>
                  <Select value={filterFamilia} onValueChange={setFilterFamilia}>
                    <SelectTrigger className="h-8 text-xs" data-testid="cruce-filter-familia"><SelectValue placeholder="Todas" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">Todas</SelectItem>
                      {data.filtros.familias.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase mb-1 block">Estado</label>
                  <Select value={filterEstado} onValueChange={setFilterEstado}>
                    <SelectTrigger className="h-8 text-xs" data-testid="cruce-filter-estado"><SelectValue placeholder="Todos" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">Todos</SelectItem>
                      <SelectItem value="coincide">Coincide</SelectItem>
                      <SelectItem value="diferencia">Diferencia</SelectItem>
                      <SelectItem value="solo_entrega">Solo Almacén</SelectItem>
                      <SelectItem value="solo_ingreso">Solo Local</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button variant="ghost" size="sm" onClick={resetFilters} className="text-xs h-8 text-muted-foreground"
                    data-testid="cruce-filter-reset">Limpiar todo</Button>
                </div>
              </div>
            )}

            <div className="overflow-auto" style={{ maxHeight: 500 }}>
              <Table>
                <TableHeader className="sticky top-0 z-10 bg-muted">
                  <TableRow>
                    {columns.map(col => (
                      <TableHead key={col.key} className={`${col.w} cursor-pointer select-none`}
                        onClick={() => toggleSort(col.key)} data-testid={`cruce-sort-${col.key}`}>
                        <div className="flex items-center gap-1 text-[11px]">
                          {col.label}
                          <ArrowUpDown className="w-3 h-3 text-muted-foreground" />
                        </div>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sorted.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={columns.length} className="text-center py-10 text-xs text-muted-foreground">
                        Sin resultados para los filtros aplicados
                      </TableCell>
                    </TableRow>
                  ) : sorted.map((row, i) => {
                    const est = ESTADO_CONFIG[row.estado] || ESTADO_CONFIG.diferencia;
                    const EstIcon = est.icon;
                    return (
                      <TableRow key={i} className={`hover:bg-muted ${row.estado === "diferencia" ? "bg-amber-50/30" : ""}`}
                        data-testid={`cruce-row-${i}`}>
                        <TableCell className="text-xs text-muted-foreground font-mono">{row.fecha}</TableCell>
                        <TableCell className="text-xs font-medium text-foreground font-mono">{row.codigo}</TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-xs truncate">{row.descripcion}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{row.familia}</TableCell>
                        <TableCell className="text-xs text-right font-mono font-medium text-[#FF9900]">
                          {row.cantidad_enviada > 0 ? row.cantidad_enviada : <span className="text-slate-300">—</span>}
                        </TableCell>
                        <TableCell className="text-xs text-right font-mono font-medium text-emerald-700">
                          {row.cantidad_recibida > 0 ? row.cantidad_recibida : <span className="text-slate-300">—</span>}
                        </TableCell>
                        <TableCell className={`text-xs text-right font-mono font-bold ${row.diferencia > 0 ? "text-emerald-600" : row.diferencia < 0 ? "text-rose-600" : "text-muted-foreground"}`}>
                          {row.diferencia > 0 ? `+${row.diferencia}` : row.diferencia === 0 ? "0" : row.diferencia}
                        </TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${est.bg} ${est.color} ${est.border} border`}>
                            <EstIcon className="w-3 h-3" />{est.label}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </Card>
        </>
      ) : null}
    </div>
  );
};

export default CruceDatos;
